import { action, computed, observable, runInAction } from 'mobx'
import { Id } from '@/infra/id'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import type { Workspace } from '@/features/iam/workspaces/Workspace'
import type { FetchTermsAcceptanceInfoFn } from '@/features/account'
import type { GetExitMethodFn } from '@/features/shell'

/**
 * When customers are brought to us through a partner via a magic link,
 * if they haven't accepted the terms yet, we'll display a hand-off experience
 * before asking for terms acceptance.
 */
export class HandoffViewModel {
  private readonly _prompt

  /**
   * Modal state.
   */
  readonly modal = new ModalViewModel()

  /**
   * Handoff information.
   */
  @observable accessor handoffInfo: HandoffInfo | null = null

  constructor(
    private readonly _fetchTermsAcceptance: FetchTermsAcceptanceInfoFn,
    private readonly _getRootExitMethod: GetExitMethodFn,
  ) {
    this._prompt = new AsyncPrompt<void>({
      abort$: this.modal.close$,
    })
  }

  /**
   * Whether the modal is showing.
   */
  @computed get isShowing() {
    return this.modal.showing
  }

  /**
   * Closes the hand-off dialog and resolves any pending {@link startHandoff} call.
   */
  @action.bound
  getStarted() {
    this._prompt.respond()
    this.modal.close()
  }

  /**
   * Navigates back to the partner.
   */
  @action.bound
  backToPartner() {
    this._getRootExitMethod().execute()
  }

  /**
   * Starts the hand-off experience.
   * Will exit quickly if not applicable.
   */
  async startHandoff(
    workspace: Workspace,
    signal?: AbortSignal,
  ): Promise<void> {
    const exitMethod = this._getRootExitMethod()
    const isCancelAllowed = exitMethod.isVisible
    const handoffInfo = getHandoffInfo(workspace, isCancelAllowed)

    if (!handoffInfo) {
      return
    }

    // Check if we've accepted the terms, or if terms are not applicable,
    // in which case we don't need to show the handoff experience.
    const acceptance = await this._fetchTermsAcceptance(
      handoffInfo.workspaceId,
      signal,
    )
    if (acceptance === 'Accepted' || acceptance === 'NotApplicable') {
      return
    }

    // Show the handoff dialog.
    runInAction(() => {
      this.handoffInfo = handoffInfo
      this.modal.show()
    })

    // Wait for the user to continue.
    await this._prompt.prompt(signal)
  }
}

/**
 * Handoff info for the dialog.
 */
export interface HandoffInfo {
  /**
   * Id of the workspace.
   */
  readonly workspaceId: string
  /**
   * Name of the partner.
   */
  readonly name: string
  /**
   * Partner's icon URL, if applicable.
   */
  readonly iconUrl: string | null
  /**
   * Partner's inverted icon URL, if applicable.
   */
  readonly invertedIconUrl: string | null
  /**
   * Whether cancelling is allowed.
   */
  readonly isCancelAllowed: boolean
}

/**
 * Gets handoff info from the workspace and session info.
 *
 * @param workspace
 * @param isCancelAllowed
 */
function getHandoffInfo(
  workspace: Workspace,
  isCancelAllowed: boolean,
): HandoffInfo | null {
  // Only show handoff info when using magic links and external customers on
  // the workspace.
  if (
    !Id.some(workspace.id) ||
    !workspace.config.publicApi.magicLinkEnabled ||
    !workspace.config.externalCustomer.enabled
  ) {
    return null
  }

  return {
    workspaceId: workspace.id,
    name: workspace.name,
    iconUrl: workspace.branding.logos.icon,
    invertedIconUrl: workspace.branding.logos.iconWhite,
    isCancelAllowed,
  } as const
}
